// Revisão de Catálogo (Etapa 2): metadados de confirmação por variante (ou pelo produto
// sem variantes, via PRODUCT_UNIT) e o prazo de revisão persistem no MongoDB via API.
// A rotina apenas IDENTIFICA itens pendentes — a confirmação continua humana.
import { getAllProducts } from '../data/catalog';
import {
  VARIANT_STATUS,
  PRODUCT_UNIT,
  getVariantStatus,
  getOverrides,
  setVariantStatus,
  pushHistory,
  notifyAvailabilityChanged,
} from './availability';

const API = process.env.REACT_APP_BACKEND_URL;

let metaCache = {};
let reviewDaysCache = 60;
let ready = false;
let initPromise = null;

const staffRole = () => {
  try {
    return JSON.parse(localStorage.getItem('hud_admin_user') || 'null')?.role || '';
  } catch {
    return '';
  }
};

export function initReview(force = false) {
  if (initPromise && !force) return initPromise;
  initPromise = (async () => {
    try {
      const res = await fetch(`${API}/api/review`);
      if (!res.ok) throw new Error('review');
      const data = await res.json();
      metaCache = data.meta || {};
      reviewDaysCache = data.reviewDays || 60;
      ready = true;
      notifyAvailabilityChanged();
    } catch (e) {
      console.error('Revisão: API indisponível.', e);
    }
  })();
  return initPromise;
}

export const isReviewReady = () => ready;

export const getReviewDays = () => reviewDaysCache;

export function setReviewDays(days) {
  reviewDaysCache = days;
  notifyAvailabilityChanged();
  fetch(`${API}/api/review/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': staffRole() },
    body: JSON.stringify({ reviewDays: days }),
  })
    .then((r) => {
      if (!r.ok) throw new Error('review-config');
    })
    .catch(() => initReview(true));
}

// Produto sem variantes: metadados no nível do produto (chave lógica interna).
const metaId = (p, v) => `${p.slug}::${v ? v.id : PRODUCT_UNIT}`;

export function loadReviewMeta() {
  return metaCache;
}

export const daysSince = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

// Situações de revisão NÃO substituem o status de disponibilidade.
export function situationFor(status, meta, prazo) {
  if (status !== VARIANT_STATUS.DISPONIVEL) return null;
  if (meta.postponed) return 'ADIADA';
  const d = daysSince(meta.lastConfirmedAt);
  if (d < prazo) return 'EM_DIA';
  if (d < prazo + 30) return 'PENDENTE';
  return 'ATRASADA';
}

const defaultMeta = () => ({
  lastConfirmedAt: new Date().toISOString(),
  lastConfirmedBy: null,
  lastReviewedAt: null,
  postponements: 0,
  postponed: false,
});

function buildEntry(p, v) {
  const code = metaId(p, v);
  const m = metaCache[code] || defaultMeta();
  const status = getVariantStatus(p, v ? v.id : null);
  const changedAt = getOverrides()[code]?.changedAt || null;
  return {
    product: p,
    variant: v, // null em produtos sem variantes — a UI trata como produto único
    code,
    status,
    meta: m,
    days: daysSince(m.lastConfirmedAt),
    outOfStockDays: changedAt ? daysSince(changedAt) : null,
    situation: situationFor(status, m, getReviewDays()),
  };
}

export function listReviewItems() {
  return getAllProducts().flatMap((p) =>
    p.hasVariants === false ? [buildEntry(p, null)] : p.variants.map((v) => buildEntry(p, v))
  );
}

function touchMeta(p, v, patch) {
  const key = metaId(p, v);
  metaCache = { ...metaCache, [key]: { ...defaultMeta(), ...metaCache[key], ...patch } };
  notifyAvailabilityChanged();
  fetch(`${API}/api/review/meta`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Role': staffRole() },
    body: JSON.stringify({ productSlug: p.slug, variantId: v ? v.id : PRODUCT_UNIT, patch }),
  })
    .then((r) => {
      if (!r.ok) throw new Error('review-meta');
    })
    .catch(() => initReview(true));
}

const baseHistory = (p, v, user, action) => ({
  productSlug: p.slug,
  productName: p.name,
  variantId: v ? v.id : PRODUCT_UNIT,
  variantName: v ? v.name : null,
  action,
  userId: user?.id || null,
  userName: user?.name || null,
  at: new Date().toISOString(),
  origin: 'REVISÃO DE CATÁLOGO',
});

export function confirmAvailable(p, v, user) {
  const now = new Date().toISOString();
  touchMeta(p, v, {
    lastConfirmedAt: now,
    lastConfirmedBy: user?.id || null,
    lastReviewedAt: now,
    postponements: 0,
    postponed: false,
  });
  pushHistory({
    ...baseHistory(p, v, user, 'Disponibilidade confirmada'),
    from: VARIANT_STATUS.DISPONIVEL,
    to: VARIANT_STATUS.DISPONIVEL,
  });
  notifyAvailabilityChanged();
}

export function reviewMarkOutOfStock(p, v, user) {
  setVariantStatus({
    product: p,
    variantId: v ? v.id : null,
    newStatus: VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL,
    user,
    origin: 'REVISÃO DE CATÁLOGO',
    action: 'Marcado sem estoque',
  });
  touchMeta(p, v, { lastReviewedAt: new Date().toISOString(), postponed: false, postponements: 0 });
}

export function postponeReview(p, v, user) {
  const meta = metaCache[metaId(p, v)] || { postponements: 0 };
  touchMeta(p, v, {
    postponed: true,
    postponements: (meta.postponements || 0) + 1,
    lastReviewedAt: new Date().toISOString(),
  });
  pushHistory({
    ...baseHistory(p, v, user, 'Revisão adiada'),
    from: VARIANT_STATUS.DISPONIVEL,
    to: VARIANT_STATUS.DISPONIVEL,
  });
  notifyAvailabilityChanged();
}

export function backInStock(p, v, user) {
  setVariantStatus({
    product: p,
    variantId: v ? v.id : null,
    newStatus: VARIANT_STATUS.DISPONIVEL,
    user,
    origin: 'REVISÃO DE CATÁLOGO',
    action: 'Voltou ao estoque',
  });
  const now = new Date().toISOString();
  touchMeta(p, v, { lastConfirmedAt: now, lastConfirmedBy: user?.id || null, lastReviewedAt: now, postponements: 0, postponed: false });
}

export function stillOutOfStock(p, v, user) {
  touchMeta(p, v, { lastReviewedAt: new Date().toISOString() });
  pushHistory({
    ...baseHistory(p, v, user, 'Disponibilidade revisada — continua sem estoque'),
    from: VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL,
    to: VARIANT_STATUS.TEMPORARIAMENTE_INDISPONIVEL,
  });
  notifyAvailabilityChanged();
}

export function markNoRestock(p, v, user) {
  return setVariantStatus({
    product: p,
    variantId: v ? v.id : null,
    newStatus: VARIANT_STATUS.SEM_PREVISAO,
    user,
    origin: 'REVISÃO DE CATÁLOGO',
    action: 'Marcado sem previsão',
  });
}
